import React, { useState, useEffect } from "react";
import PropertyNames from "../../PropertyNames";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Alert from "@material-ui/lab/Alert";
import ActionButton from "../../components/ActionButton";
import {
  InputLabel,
  FormHelperText,
  FormControl,
  Typography,
  MenuItem,
  Select,
  Collapse,
  TextField,
  Button,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  FormControlLabel,
  Switch,
  Checkbox,
  Input,
  IconButton,
  InputAdornment,
  AppBar,
  Tabs,
  Tab,
} from "@material-ui/core";
import { setApplicationProperties } from "../../redux/actions/universalActions.js";
import axios, { taxServiceClient, authServiceClient } from "../../AxiosClient";
import { useSnackbar } from "notistack";
import { Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import "filepond/dist/filepond.min.css";
import UploadError from "../../components/messages/UploadError";
import TeamSelector from "../../components/taxonomy/TeamSelector.js"
registerPlugin(FilePondPluginFileValidateType);
registerPlugin(FilePondPluginFileValidateSize);
const acceptedFileTypes = ["text/xml", "application/xml"];
const acceptedFileTypesCertificate = [
  "application/pkix-cert",
  "application/x-x509-ca-cert",
  "application/x-x509-user-cert",
  "application/x-pem-file",
  "text/plain",
];
const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: 15,
    display: "flex",
  },
  container: {
    height: "100%",
    width: "100%",
  },
  flexContainer: {
    display: "flex",
  },
  textFieldLeft: {
    marginRight: "20px",
    width: "100%",
    marginTop: 14,
  },
  textFieldRight: {
    width: "100%",
    marginTop: 14,
  },
}));
function AuthenticationSettings() {
  const properties = useSelector((state) => ({
    ...state.setting.applicationProperties,
  }));
  const apiLocation = useSelector((state) => state.setting.apiLocation);
  const organization = useSelector((state) => {
    if (
      !state.user.currentUser.organization ||
      !state.user.currentUser.organization.organizationId
    ) {
      return "demo";
    }
    return state.user.currentUser.organization.organizationId;
  });
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [updatedProperties, setUpdatedProperties] = useState([]);
  const [showSecret, setShowSecret] = useState(false);
  const [attributeMappings, setAttributeMappings] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [certificateFiles, setCertificateFiles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    taxServiceClient.get("/role/").then((res) => {
      setRoles(res.data);
    });
    // taxServiceClient.get("/team/").then((res) => {
    //   setTeams(res.data.content);
    // });
    authServiceClient.get("/attribute-mapping/").then((res) => {
      setAttributeMappings(res.data);
    });
  }, []);

  const getTransKeyFromHttpStatus = (status, message) => {
    let translationKey = "unexpected_upload_error";
    switch (status) {
      case 409:
        translationKey = "virus_detected_in_file";
        break;
      case 413:
        translationKey = "file_too_big_to_process";
        break;
      case 418:
        // I'm a teapot.
        // Temporary method to handle internal 500 errors. See UV6-1207
        // Error type is propagated as a translation key
        // First characters are the Http status it prepresents
        // e.g. 500_AV_NOT_DEFINED
        if (!!message && message.length > 0) {
          translationKey = message;
        }
    }
    return translationKey;
  };

  const handleChange = (propertyKey) => (e) => {
    updateProperties(propertyKey, e.target.value);
  };

  const handleCheckboxChange = (propertyKey) => (e) => {
    updateProperties(propertyKey, e.target.checked.toString());
  };

  const updateProperties = (propertyKey, propertyValue) => {
    if (updatedProperties.indexOf(propertyKey) === -1) {
      setUpdatedProperties([...updatedProperties, propertyKey]);
    }
    dispatch(
      setApplicationProperties({
        ...properties,
        [propertyKey]: propertyValue,
      })
    );
  };

  const save = () => {
    setSaving(true);
    let updated = {};
    // so we only patch the changed props and not everything...
    for (const key of updatedProperties) {
      updated[key] = properties[key];
    }
    let propUpdate = axios.patch("/application-property/", {
      ...updated,
      [PropertyNames.SAML_BASE_URL]: `https://${apiLocation}/authservice`,
      [PropertyNames.SAML_REDIRECT_URI]: `${window.location.protocol}//${
        window.location.hostname
      }${
        window.location.port ? ":" + window.location.port : ""
      }/oauth2/redirect`,
    });
    let attributeUpdate = authServiceClient.post("/attribute-mapping/", [
      ...attributeMappings,
    ]);
    Promise.all([propUpdate, attributeUpdate])
      .then(() => {
        enqueueSnackbar(t("properties_updated_snackbar"), {
          variant: "success",
        });
        setSaving(false);
      })
      .catch((err) => {
        enqueueSnackbar(t("properties_updated_error_snackbar"), {
          variant: "error",
        });
        setSaving(false);
      });
  };

  const authTabs = () => {
    return (
      <>
        <Tabs
          value={selectedTab}
          onChange={(e, nv) => setSelectedTab(nv)}
          style={{ marginBottom: 10 }}
        >
          <Tab label={t("auth_settings_tab_label")} />
          <Tab label={t("attribute_mappings_tab_label")} />
        </Tabs>
      </>
    );
  };

  const updateAttributeMappingType = (idx) => (e) => {
    let attrs = [...attributeMappings];
    attrs = attrs.map((item, attrIdx) => {
      if (attrIdx === idx) {
        return { ...item, attributeType: e.target.value };
      }
      return item;
    });
    setAttributeMappings(attrs);
  };

  const updateAttributeMappingExternalAttribute = (idx) => (e) => {
    let attrs = [...attributeMappings];
    attrs = attrs.map((item, attrIdx) => {
      if (attrIdx === idx) {
        return { ...item, externalAttribute: e.target.value };
      }
      return item;
    });
    setAttributeMappings(attrs);
  };

  const updateAttributeMappingInternalValue = (idx) => (e) => {
    let attrs = [...attributeMappings];
    attrs = attrs.map((item, attrIdx) => {
      if (attrIdx === idx) {
        return { ...item, internalValue: e.target.value };
      }
      return item;
    });
    setAttributeMappings(attrs);
  };

  // const updateAttributeMappingInternalValueTeam = (idx) => (e) => {
  //   let attrs = [...attributeMappings];
  //   attrs = attrs.map((item, attrIdx) => {
  //     if (attrIdx === idx) {
  //       return { ...item, internalValue: e.teamId };
  //     }
  //     return item;
  //   });
  //   setAttributeMappings(attrs);
  // };

  const updateAttributeMappingInternalValueTeam = (idx) => (e) => {
    let result = {};
    result.target = {};
    result.target.value = e.teamId;
    updateAttributeMappingInternalValue(idx)(result);
  };

  const deleteAttributeMapping = (idx) => () => {
    let attrs = [...attributeMappings];
    let removed = attrs.splice(idx, 1)[0];
    if (removed && removed.mappingId) {
      authServiceClient.delete(`/attribute-mapping/${removed.mappingId}`);
    }
    setAttributeMappings(attrs);
  };

  const attributeMappingComponent = () => {
    return (
      <div>
        <Typography variant="h6" gutterBottom>
          {t("auth_settings_attributes_header")}
        </Typography>
        {properties[PropertyNames.EXTERNAL_AUTH_CONTAINS_USER_STATE] ===
          "true" ||
        properties[PropertyNames.EXTERNAL_AUTH_CONTAINS_USER_STATE] === true ? (
          <>
            <Typography variant="body2" gutterBottom>
              {t("auth_settings_attributes_subheader")}
            </Typography>
            <div style={{ display: "flex" }}>
              <div
                className={classes.flexContainer}
                style={{
                  flexDirection: "column",
                  flex: 0.5,
                  borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                  paddingRight: 15,
                }}
              >
                <Typography variant="body2" style={{ marginTop: 10 }}>
                  <b>{t("auth_settings_attributes_attribute_header")}</b>
                </Typography>
                <TextField
                  label={t("auth_attribute_username_label")}
                  value={properties[PropertyNames.AUTH_ATTRIBUTE_USERNAME]}
                  onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_USERNAME)}
                  margin="dense"
                />
                <TextField
                  label={t("auth_attribute_firstname_label")}
                  value={properties[PropertyNames.AUTH_ATTRIBUTE_FIRSTNAME]}
                  onChange={handleChange(
                    PropertyNames.AUTH_ATTRIBUTE_FIRSTNAME
                  )}
                  margin="dense"
                />
                <TextField
                  label={t("auth_attribute_lastname_label")}
                  value={properties[PropertyNames.AUTH_ATTRIBUTE_SURNAME]}
                  onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_SURNAME)}
                  margin="dense"
                />
                <TextField
                  label={t("auth_attribute_email_label")}
                  value={properties[PropertyNames.AUTH_ATTRIBUTE_EMAIL]}
                  onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_EMAIL)}
                  margin="dense"
                />
                <TextField
                  label={t("auth_attribute_teams_label")}
                  value={properties[PropertyNames.AUTH_ATTRIBUTE_TEAMS]}
                  onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_TEAMS)}
                  margin="dense"
                />
                <TextField
                  label={t("auth_attribute_roles_label")}
                  value={properties[PropertyNames.AUTH_ATTRIBUTE_ROLES]}
                  onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_ROLES)}
                  margin="dense"
                />
              </div>
              <div style={{ flex: 1, paddingLeft: 15 }}>
                <Typography variant="body2" style={{ marginTop: 10 }}>
                  <b>{t("auth_settings_attributes_mappings_header")}</b>
                </Typography>
                {attributeMappings.map((item, idx) => {
                  return (
                    <div key={idx} style={{ display: "flex" }}>
                      <Typography style={{ marginTop: 20, marginRight: 5 }}>
                        For
                      </Typography>
                      <TextField
                        label=""
                        select
                        value={attributeMappings[idx].attributeType}
                        onChange={updateAttributeMappingType(idx)}
                        style={{ marginTop: 16, marginRight: 20, width: "10%" }}
                      >
                        <MenuItem value="ROLE">
                          {/* <Trans i18nKey="global_role_pural" /> */}
                         Role
                        </MenuItem>
                        <MenuItem value="TEAM">
                          {/* <Trans i18nKey="global_team_pural" /> */}
                          Team
                        </MenuItem>
                      </TextField>
                      <TextField
                        label="External attribute"
                        value={attributeMappings[idx].externalAttribute}
                        onChange={updateAttributeMappingExternalAttribute(idx)}
                        style={{ width: "50%" }}
                      />
                      <Typography
                        style={{
                          marginTop: 20,
                          marginRight: 10,
                          marginLeft: 10,
                        }}
                      >
                        maps to
                      </Typography>
                      {attributeMappings[idx].attributeType === "TEAM" && (
                        <TeamSelector onChange={updateAttributeMappingInternalValueTeam(idx)} value={attributeMappings[idx].internalValue}></TeamSelector>
                      )}
                      {attributeMappings[idx].attributeType === "ROLE" && (
                        <TextField
                          label=""
                          select
                          value={attributeMappings[idx].internalValue}
                          onChange={updateAttributeMappingInternalValue(idx)}
                          style={{ marginTop: 16 }}
                        >
                          {roles.map((role, idx) => {
                            return (
                              <MenuItem
                                key={`role-${idx}`}
                                value={role.bundleKey}
                              >
                                {role.description}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      )}
                      <IconButton onClick={deleteAttributeMapping(idx)}>
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  );
                })}
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setAttributeMappings([
                        ...attributeMappings,
                        {
                          id: "",
                          attributeType: "ROLE",
                        },
                      ])
                    }
                  >
                    {t("auth_settings_attributes_new_mapping")}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <Typography variant="body2">
              {t("attribute_mappings_disabled_idp_no_state")}
            </Typography>

            <div
              className={classes.flexContainer}
              style={{
                flexDirection: "column",
                flex: 0.5,
                borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                paddingRight: 15,
              }}
            >
              <FormControl>
                <InputLabel
                  className={classes.formControl}
                  id="userTypeSelectLabel"
                >
                  <Trans i18nKey="user_type" />
                </InputLabel>
                <Select
                  labelId="userTypeSelectLabel"
                  value={properties[PropertyNames.AUTH_DEFAULT_USER_TYPE]}
                  id="user-type"
                  onChange={handleChange(PropertyNames.AUTH_DEFAULT_USER_TYPE)}
                  className={classes.formControl}
                >
                  <MenuItem value="named">{t("user_type_named_user")}</MenuItem>
                  <MenuItem value="concurrent">
                    {t("user_type_concurrent_user")}
                  </MenuItem>
                  <MenuItem value="limited">{t("role_limitedUser")}</MenuItem>
                </Select>
                <FormHelperText>
                  Set the default user type for external users
                </FormHelperText>
              </FormControl>
              <TextField
                label={t("auth_attribute_username_label")}
                value={properties[PropertyNames.AUTH_ATTRIBUTE_USERNAME]}
                onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_USERNAME)}
                margin="dense"
              />
              <TextField
                label={t("auth_attribute_firstname_label")}
                value={properties[PropertyNames.AUTH_ATTRIBUTE_FIRSTNAME]}
                onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_FIRSTNAME)}
                margin="dense"
              />
              <TextField
                label={t("auth_attribute_lastname_label")}
                value={properties[PropertyNames.AUTH_ATTRIBUTE_SURNAME]}
                onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_SURNAME)}
                margin="dense"
              />
              <TextField
                label={t("auth_attribute_email_label")}
                value={properties[PropertyNames.AUTH_ATTRIBUTE_EMAIL]}
                onChange={handleChange(PropertyNames.AUTH_ATTRIBUTE_EMAIL)}
                margin="dense"
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const adminLogin = `${window.location.protocol}//${window.location.hostname}${
    window.location.port ? ":" + window.location.port : ""
  }/login/admin`;
  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ marginRight: 10 }}>
          {t("authentication_settings_header")}
        </Typography>
        <ActionButton
          variant="contained"
          color="primary"
          onClick={save}
          loading={saving}
          id="save-configuration"
        >
          {t("global_save")}
        </ActionButton>
      </div>
      <div style={{ width: "100%" }}>
        <Typography variant="h6" gutterBottom>
          {"User settings"}
        </Typography>
        <Typography variant="body2" gutterBottom>
          {t("auth_method_header")}
        </Typography>
        <div style={{ display: "flex" }}>
          <FormControl
            style={{ width: "25%", marginBottom: 10, marginRight: 50 }}
          >
            <InputLabel id="auth-method-label">
              {t("auth_method_label")}
            </InputLabel>
            <Select
              labelId="auth-method-label"
              id="auth-method"
              value={properties[PropertyNames.AUTHENTICATION_TYPE]}
              onChange={handleChange(PropertyNames.AUTHENTICATION_TYPE)}
            >
              <MenuItem value="database">{t("auth_method_database")}</MenuItem>
              <MenuItem value="ldap">{t("auth_method_ldap")}</MenuItem>
              <MenuItem value="oidc">{t("auth_method_oidc")}</MenuItem>
              <MenuItem value="saml">{t("auth_method_saml")}</MenuItem>
            </Select>
            <FormHelperText>{t("auth_method_helper_text")}</FormHelperText>
          </FormControl>
          {properties[PropertyNames.AUTHENTICATION_TYPE] !== "database" &&
            properties[PropertyNames.AUTHENTICATION_TYPE] !== "ldap" && (
              <FormControl style={{ marginTop: 10 }}>
                <FormControlLabel
                  label={t("auth_allow_database_logins")}
                  control={
                    <Checkbox
                      value=""
                      checked={
                        properties[PropertyNames.AUTH_ALLOW_DATABASE_LOGINS] ===
                        "true"
                      }
                      onChange={handleCheckboxChange(
                        PropertyNames.AUTH_ALLOW_DATABASE_LOGINS
                      )}
                      color="primary"
                    />
                  }
                />
                <FormHelperText>
                  {t("auth_allow_database_logins_helper_text")}
                </FormHelperText>
              </FormControl>
            )}
        </div>
        {properties[PropertyNames.AUTHENTICATION_TYPE] !== "database" &&
          properties[PropertyNames.AUTH_ALLOW_DATABASE_LOGINS] === "false" && (
            <Alert severity="warning" style={{ marginBottom: 10 }}>
              {t("warning_disabled_database_logins_dialog_hidden")}
              <br />
              {t("warning_disabled_database_logins_admin_users")}{" "}
              <a href={adminLogin}>{adminLogin}</a>
            </Alert>
          )}
        {properties[PropertyNames.AUTHENTICATION_TYPE] !== "database" && (
          <FormControl>
            <FormControlLabel
              label={t("auth_idp_contains_user_state_label")}
              control={
                <Checkbox
                  value=""
                  checked={
                    properties[
                      PropertyNames.EXTERNAL_AUTH_CONTAINS_USER_STATE
                    ] === "true"
                  }
                  onChange={handleCheckboxChange(
                    PropertyNames.EXTERNAL_AUTH_CONTAINS_USER_STATE
                  )}
                  color="primary"
                />
              }
            />
            <FormHelperText>
              {t("auth_idp_contains_user_state_helper_text")}
            </FormHelperText>
          </FormControl>
        )}
        <Collapse in={properties[PropertyNames.AUTHENTICATION_TYPE] === "ldap"}>
          <>
            {authTabs()}
            {selectedTab === 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  {t("ldap_settings_basic_header")}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {t("ldap_settings_basic_subheader")}
                </Typography>
                <div className={classes.flexContainer}>
                  <TextField
                    style={{ marginRight: 20, minWidth: "25%" }}
                    label={t("ldap_url_label")}
                    value={properties[PropertyNames.LDAP_PROVIDER_URL]}
                    onChange={handleChange(PropertyNames.LDAP_PROVIDER_URL)}
                    helperText={t("ldap_url_helper_text")}
                    margin="dense"
                  />
                  <TextField
                    label={t("ldap_base_dn")}
                    value={properties[PropertyNames.LDAP_SCHEMA_ROOT]}
                    onChange={handleChange(PropertyNames.LDAP_SCHEMA_ROOT)}
                    helperText={t("ldap_base_dn_helper_text")}
                    margin="dense"
                  />
                </div>
                <div className={classes.flexContainer}>
                  <TextField
                    style={{ marginRight: 20 }}
                    label={t("ldap_search_user")}
                    value={properties[PropertyNames.LDAP_SECURITY_PRINCIPAL]}
                    onChange={handleChange(
                      PropertyNames.LDAP_SECURITY_PRINCIPAL
                    )}
                    helperText={t("ldap_search_user_helper_text")}
                    margin="dense"
                  />
                  <TextField
                    type="password"
                    label={t("ldap_search_user_password")}
                    value={properties[PropertyNames.LDAP_SECURITY_CREDENTIALS]}
                    onChange={handleChange(
                      PropertyNames.LDAP_SECURITY_CREDENTIALS
                    )}
                    helperText={t("ldap_search_user_password_helper_text")}
                    margin="dense"
                  />
                </div>
              </>
            )}
            {selectedTab === 1 && attributeMappingComponent()}
          </>
        </Collapse>
        <Collapse in={properties[PropertyNames.AUTHENTICATION_TYPE] === "oidc"}>
          {authTabs()}
          {selectedTab === 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                {t("oidc_settings_basic_header")}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {t("oidc_settings_basic_subheader")}
              </Typography>
              {/* <FormControl style={{ minWidth: "15%", marginBottom: 10 }}>
                <InputLabel id="oidc-provider-label">
                  {t("oidc_provider_label")}
                </InputLabel>
                <Select
                  labelId="oidc-method-label"
                  id="oidc-method"
                  value={properties[PropertyNames.OIDC_PROVIDER]}
                  onChange={handleChange(PropertyNames.OIDC_PROVIDER)}
                >
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="microsoft">Microsoft</MenuItem>
                  <MenuItem value="custom">
                    {t("oidc_custom_provider_label")}
                  </MenuItem>
                </Select>
              </FormControl> */}
              {/* <Collapse
                in={
                  properties[PropertyNames.OIDC_PROVIDER] === "google" ||
                  properties[PropertyNames.OIDC_PROVIDER] === "microsoft"
                }
              >
                <TextField
                  label={t("oidc_custom_domain_tenant_label")}
                  value={properties[PropertyNames.OIDC_PROVIDER_CUSTOM_DOMAIN]}
                  onChange={handleChange(
                    PropertyNames.OIDC_PROVIDER_CUSTOM_DOMAIN
                  )}
                  helperText={t("oidc_custom_domain_tenant_helper_text")}
                  id="tenant-id"
                  margin="normal"
                />
              </Collapse> */}
              <Typography variant="body1">
                <b>{t("oidc_settings_custom_header")}</b>
              </Typography>
              <TextField
                required
                label={t("oidc_custom_name")}
                value={properties[PropertyNames.OIDC_PROVIDER_CUSTOM_NAME]}
                onChange={handleChange(PropertyNames.OIDC_PROVIDER_CUSTOM_NAME)}
                helperText={t("oidc_custom_name_helper_text")}
                margin="normal"
              />
              <Typography variant="body2">
                {t("oidc_settings_custom_subheader")}
              </Typography>
              <ul>
                <li>
                  <Typography variant="body2">
                    <b>Application type:</b> Web application
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Sign-on method:</b> OIDC (OpenID Connect)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Login redirect URIs:</b>
                    {` https://${apiLocation}/oauth2/callback/${organization.toLowerCase()}`}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Initiate login URI (optional):</b>{" "}
                    {`${window.location.protocol}//${window.location.hostname}${
                      window.location.port ? ":" + window.location.port : ""
                    }/oidc/login`}
                  </Typography>
                </li>
              </ul>

              <Typography variant="body2" gutterBottom>
                {t("oidc_settings_custom_once_added")}
              </Typography>
              <div>
                <TextField
                  style={{ marginRight: 20 }}
                  label={t("oidc_domain")}
                  value={properties[PropertyNames.OIDC_PROVIDER_CUSTOM_DOMAIN]}
                  onChange={handleChange(
                    PropertyNames.OIDC_PROVIDER_CUSTOM_DOMAIN
                  )}
                  helperText={t("oidc_domain_helper_text")}
                />
              </div>
              <div>
                <TextField
                  style={{
                    marginRight: 20,
                    minWidth: "25%",
                    marginBottom: 10,
                  }}
                  label={t("oidc_client_id")}
                  value={
                    properties[PropertyNames.OIDC_PROVIDER_CUSTOM_CLIENT_ID]
                  }
                  onChange={handleChange(
                    PropertyNames.OIDC_PROVIDER_CUSTOM_CLIENT_ID
                  )}
                />
                <FormControl style={{ minWidth: "25%", marginBottom: 10 }}>
                  <InputLabel htmlFor="client-secret">
                    {t("oidc_client_secret")}
                  </InputLabel>
                  <Input
                    id="client-secret"
                    label={t("oidc_client_secret")}
                    type={showSecret ? "text" : "password"}
                    value={
                      properties[
                        PropertyNames.OIDC_PROVIDER_CUSTOM_CLIENT_SECRET
                      ]
                    }
                    onChange={handleChange(
                      PropertyNames.OIDC_PROVIDER_CUSTOM_CLIENT_SECRET
                    )}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle secret visibility"
                          onClick={() => setShowSecret(!showSecret)}
                          onMouseDown={(e) => e.preventDefault}
                        >
                          {showSecret ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <FormHelperText>
                    {t("oidc_client_secret_helper_text")}
                  </FormHelperText>
                </FormControl>
              </div>
            </>
          )}
          {selectedTab === 1 && attributeMappingComponent()}
        </Collapse>
        <Collapse in={properties[PropertyNames.AUTHENTICATION_TYPE] === "saml"}>
          {authTabs()}
          {selectedTab === 0 && (
            <>
              <Typography variant="body1">
                <b>{t("saml_settings_custom_header")}</b>
              </Typography>
              <Typography variant="body2">
                {t("saml_settings_custom_subheader")}
              </Typography>
              <ul>
                <li>
                  <Typography variant="body2">
                    <b>Application type:</b> Web application
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Sign-on method:</b> SAML (Security Assertion Markup
                    Language)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Single sign on URL:</b>{" "}
                    {`https://${apiLocation}/authservice/saml/SSO/alias/${organization.toLowerCase()}`}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <b>Audience URI (SP Entity ID):</b>{" "}
                    {`urn:com:kpsol:${organization.toLowerCase()}`}
                  </Typography>
                </li>
              </ul>

              <Typography variant="body2" gutterBottom>
                {t("saml_settings_custom_once_added")}
              </Typography>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <TextField
                  style={{ marginRight: 20, minWidth: "25%", marginBottom: 10 }}
                  label={t("saml_idp_issuer_label")}
                  value={properties[PropertyNames.SAML_IDP_ISSUER]}
                  onChange={handleChange(PropertyNames.SAML_IDP_ISSUER)}
                  helperText={t("saml_idp_issuer_helper_text")}
                />
                <Typography style={{ marginBottom: 10 }}>
                  Provide a URL to or upload your IdP's metadata. This is also
                  known as "dynamic configuration metadata".
                </Typography>
                <div style={{ display: "flex" }}>
                  <TextField
                    style={{
                      marginRight: 50,
                      minWidth: "25%",
                      marginBottom: 10,
                    }}
                    label={t("saml_idp_metadata_label")}
                    value={properties[PropertyNames.SAML_IDP_METADATA_URL]}
                    onChange={handleChange(PropertyNames.SAML_IDP_METADATA_URL)}
                    helperText={t("saml_idp_metadata_helper_text")}
                  />
                  <div style={{ flex: 1 }}>
                    <FilePond
                      files={files}
                      allowMultiple={false}
                      allowRevert={true}
                      acceptedFileTypes={acceptedFileTypes}
                      allowSyncAcceptAttribute={false}
                      labelFileTypeNotAllowed={t("invalid_file_type")}
                      fileValidateTypeLabelExpectedTypes={t(
                        "only_xml_types_are_supported"
                      )}
                      maxFileSize="500KB"
                      server={{
                        process: (
                          fieldName,
                          file,
                          metadata,
                          load,
                          error,
                          progress,
                          abort,
                          transfer,
                          options
                        ) => {
                          let timeout = null;
                          const formData = new FormData();
                          formData.append("file", file, file.name);
                          const request = new XMLHttpRequest();
                          request.withCredentials = true
                          request.open(
                            "POST",
                            `https://${apiLocation}/authservice/metadata/${organization}`
                          );
                          request.upload.onprogress = (e) => {
                            progress(e.lengthComputable, e.loaded, e.total);
                          };
                          request.onload = () => {
                            let payload = JSON.parse(request.responseText);
                            if (request.status >= 200 && request.status < 300) {
                              load(payload.id);
                            } else {
                              let key = getTransKeyFromHttpStatus(
                                request.status,
                                payload.message
                              );
                              error(t(key));
                              setUploadError(key);
                            }
                          };
                          request.send(formData);
                          return {
                            abort: () => {
                              request.abort();
                              abort();
                            },
                          };
                        },
                      }}
                      onprocessfile={(_err, file) => {
                        updateProperties(
                          PropertyNames.SAML_IDP_METADATA_URL,
                          ""
                        );
                      }}
                      onupdatefiles={(fileItems) => {
                        setFiles(fileItems.map((fileItem) => fileItem.file));
                      }}
                    />
                  </div>
                </div>
              </div>
              <Typography style={{ marginBottom: 10 }}>
                Upload your IdP's SAML signing certificate as a Base64-encoded
                X.509 certificate (.pem, .crt, .cer)
              </Typography>
              <div style={{ flex: 1 }}>
                <FilePond
                  files={certificateFiles}
                  allowMultiple={false}
                  allowRevert={true}
                  acceptedFileTypes={acceptedFileTypesCertificate}
                  allowSyncAcceptAttribute={false}
                  labelFileTypeNotAllowed={t("invalid_file_type")}
                  fileValidateTypeLabelExpectedTypes={t(
                    "only_x509_certificate_types_are_supported"
                  )}
                  maxFileSize="500KB"
                  fileValidateTypeDetectType={(source, type) =>
                    new Promise((res, rej) => {
                      if (!type) {
                        res("text/plain");
                      } else {
                        res(type);
                      }
                    })
                  }
                  server={{
                    process: (
                      fieldName,
                      file,
                      metadata,
                      load,
                      error,
                      progress,
                      abort,
                      transfer,
                      options
                    ) => {
                      let timeout = null;
                      const formData = new FormData();
                      formData.append("file", file, file.name);
                      const request = new XMLHttpRequest();
                      request.withCredentials = true
                      request.open(
                        "POST",
                        `https://${apiLocation}/authservice/certificate/${organization}`
                      );
                      request.upload.onprogress = (e) => {
                        progress(e.lengthComputable, e.loaded, e.total);
                      };
                      request.onload = () => {
                        let payload = JSON.parse(request.responseText);
                        if (request.status >= 200 && request.status < 300) {
                          load(payload.id);
                        } else {
                          let key = getTransKeyFromHttpStatus(
                            request.status,
                            payload.message
                          );
                          error(t(key));
                          setUploadError(key);
                        }
                      };
                      request.send(formData);
                      return {
                        abort: () => {
                          request.abort();
                          abort();
                        },
                      };
                    },
                  }}
                  onprocessfile={(_err, file) => {
                    updateProperties(PropertyNames.SAML_CERTIFICATE, "");
                  }}
                  onupdatefiles={(fileItems) => {
                    setCertificateFiles(
                      fileItems.map((fileItem) => fileItem.file)
                    );
                  }}
                />
              </div>

              <FormControl style={{ marginBottom: 10 }}>
                <FormControlLabel
                  label={t("saml_global_logout_label")}
                  control={
                    <Checkbox
                      value=""
                      checked={
                        properties[PropertyNames.SAML_GLOBAL_LOGOUT] === "true"
                      }
                      onChange={handleCheckboxChange(
                        PropertyNames.SAML_GLOBAL_LOGOUT
                      )}
                      color="primary"
                    />
                  }
                />
                <FormHelperText>
                  {t("saml_global_logout_helper_text")}
                </FormHelperText>
              </FormControl>
            </>
          )}
          {selectedTab === 1 && attributeMappingComponent()}
        </Collapse>
      </div>
      <UploadError
        uploadError={uploadError}
        setUploadError={() => setUploadError(false)}
      />
    </div>
  );
}

export default AuthenticationSettings;
